/**
 * Timezone-aware helpers for Prosperity Mentors event sessions.
 * pm_event_sessions stores starts_at / ends_at as UTC timestamptz plus an IANA `timezone`;
 * every CRM surface that edits or shows a session time goes through these so the
 * wall-clock time and abbreviation (EDT/EST, CDT/CST, …) always follow the session's zone.
 *
 * KEEP IN SYNC: the display section (abbreviations, formatSessionTimes,
 * formatSessionTimesCompact, sessionTimeMergeFields) is mirrored for Deno in
 * supabase/functions/_shared/pmEventTime.ts. Run `node scripts/check-pm-event-time.mjs`
 * after changing either copy.
 */

export const PM_DEFAULT_TZ = 'America/New_York';

export const PM_TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'America/Mexico_City', label: 'Mexico City' },
  { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
  { value: 'UTC', label: 'UTC' },
];

function isValidTimeZone(tz) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Session timezone, falling back to Eastern when missing or not a valid IANA name. */
export function resolvePmTimezone(tz) {
  const trimmed = typeof tz === 'string' ? tz.trim() : '';
  return trimmed && isValidTimeZone(trimmed) ? trimmed : PM_DEFAULT_TZ;
}

/** Curated options, plus the session's current zone if it isn't one of them. */
export function pmTimezoneOptionsFor(currentTz) {
  const tz = typeof currentTz === 'string' ? currentTz.trim() : '';
  if (!tz || PM_TIMEZONE_OPTIONS.some((o) => o.value === tz)) return PM_TIMEZONE_OPTIONS;
  return [...PM_TIMEZONE_OPTIONS, { value: tz, label: tz }];
}

function toDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Numeric wall-clock parts of an instant in `tz`. */
function zonedParts(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Some engines render midnight as "24" even with h23.
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

/** Offset (ms) of `tz` from UTC at the given instant; positive east of UTC. */
function tzOffsetMs(date, tz) {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const wholeSecondMs = date.getTime() - (date.getTime() % 1000);
  return asUtc - wholeSecondMs;
}

/** UTC ISO → `YYYY-MM-DDTHH:mm` wall-clock in `tz`, for datetime-local inputs. */
export function isoToZonedInputValue(iso, tz) {
  const d = toDate(iso);
  if (!d) return '';
  const p = zonedParts(d, resolvePmTimezone(tz));
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * `YYYY-MM-DDTHH:mm` typed as wall-clock time in `tz` → UTC ISO.
 * DST-correct: the offset is taken at the target instant, not "now".
 */
export function zonedInputValueToIso(value, tz) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value).trim());
  if (!m) return null;
  const zone = resolvePmTimezone(tz);
  const [, y, mo, d, h, mi, s] = m;
  const wallAsUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s || 0));
  if (Number.isNaN(wallAsUtc)) return null;

  const firstGuess = wallAsUtc - tzOffsetMs(new Date(wallAsUtc), zone);
  const secondOffset = tzOffsetMs(new Date(firstGuess), zone);
  const result = wallAsUtc - secondOffset;
  return new Date(result).toISOString();
}

// ---------------------------------------------------------------------------
// Display (mirrored in supabase/functions/_shared/pmEventTime.ts)
// ---------------------------------------------------------------------------

/** US reference zones shown next to every session, in display order. */
const PM_REFERENCE_ZONES = ['America/New_York', 'America/Chicago', 'America/Los_Angeles'];

/** Zones where Intl's short name is "GMT-3"-style or ambiguous; US zones + Anchorage use Intl. */
const PM_TZ_ABBREVIATIONS = {
  'America/Argentina/Buenos_Aires': 'ART',
  'America/Bogota': 'COT',
  'America/Mexico_City': 'CST',
  'America/Puerto_Rico': 'AST',
  'America/Phoenix': 'MST',
  'Pacific/Honolulu': 'HST',
  UTC: 'UTC',
};

/** Short zone abbreviation at that instant, e.g. "EDT" in July, "EST" in December. */
export function pmTimezoneAbbreviation(iso, tz) {
  const zone = resolvePmTimezone(tz);
  if (PM_TZ_ABBREVIATIONS[zone]) return PM_TZ_ABBREVIATIONS[zone];
  const d = toDate(iso) || new Date();
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

function fmt(date, tz, options) {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, ...options }).format(date);
}

function timeLabel(date, tz) {
  return fmt(date, tz, { hour: 'numeric', minute: '2-digit', hour12: true });
}

function dateKey(date, tz) {
  const p = zonedParts(date, tz);
  return `${p.year}-${p.month}-${p.day}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function zoneEntry(start, end, tz, primaryDateKey) {
  const startAbbr = pmTimezoneAbbreviation(start, tz);
  let range;
  if (end) {
    const endAbbr = pmTimezoneAbbreviation(end, tz);
    range = endAbbr === startAbbr
      ? `${timeLabel(start, tz)} – ${timeLabel(end, tz)} ${startAbbr}`
      : `${timeLabel(start, tz)} ${startAbbr} – ${timeLabel(end, tz)} ${endAbbr}`;
  } else {
    range = `${timeLabel(start, tz)} ${startAbbr}`;
  }
  let startLabel = `${timeLabel(start, tz)} ${startAbbr}`;
  if (primaryDateKey && dateKey(start, tz) !== primaryDateKey) {
    const rollover = ` (${fmt(start, tz, { month: 'short', day: 'numeric' })})`;
    range += rollover;
    startLabel += rollover;
  }
  return {
    entry: {
      tz,
      abbr: startAbbr,
      dateLabel: fmt(start, tz, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      timeRangeLabel: range.trim(),
    },
    startLabel: startLabel.trim(),
  };
}

function buildSessionTimes(startsAt, endsAt, eventTimezone) {
  const start = toDate(startsAt);
  if (!start) return null;
  const endRaw = toDate(endsAt);
  const end = endRaw && endRaw.getTime() > start.getTime() ? endRaw : null;
  const primaryTz = resolvePmTimezone(eventTimezone);
  const primaryDateKey = dateKey(start, primaryTz);

  const primary = zoneEntry(start, end, primaryTz, null);
  const others = PM_REFERENCE_ZONES
    .filter((tz) => tz !== primaryTz)
    .map((tz) => zoneEntry(start, end, tz, primaryDateKey));
  return { primary, others };
}

/**
 * Same moment in the session's zone (primary) plus ET / CT / PT (minus the primary).
 * Other zones whose local date differs from the primary's get "(Oct 5)" appended.
 * Returns null when startsAt is missing/invalid.
 *
 * @returns {{
 *   primary: { tz: string, abbr: string, dateLabel: string, timeRangeLabel: string },
 *   others: { tz: string, abbr: string, dateLabel: string, timeRangeLabel: string }[],
 *   plainText: string,
 *   html: string,
 * } | null}
 */
export function formatSessionTimes(startsAt, endsAt, eventTimezone) {
  const built = buildSessionTimes(startsAt, endsAt, eventTimezone);
  if (!built) return null;
  const primary = built.primary.entry;
  const others = built.others.map((o) => o.entry);
  const othersLine = others.map((o) => o.timeRangeLabel).join(' · ');
  const plainText = othersLine ? `${primary.timeRangeLabel}\n${othersLine}` : primary.timeRangeLabel;
  const primaryHtml = `<span style="font-size:18px;font-weight:700;">${escapeHtml(primary.timeRangeLabel)}</span>`;
  const html = othersLine
    ? `${primaryHtml}<br><span style="font-size:13px;color:#6b7280;">${escapeHtml(othersLine)}</span>`
    : primaryHtml;
  return { primary, others, plainText, html };
}

/** Single-line form: "10:00 AM – 12:00 PM EDT (also 9:00 AM CDT · 7:00 AM PDT)". */
export function formatSessionTimesCompact(startsAt, endsAt, eventTimezone) {
  const built = buildSessionTimes(startsAt, endsAt, eventTimezone);
  if (!built) return '';
  const also = built.others.map((o) => o.startLabel).join(' · ');
  return also ? `${built.primary.entry.timeRangeLabel} (also ${also})` : built.primary.entry.timeRangeLabel;
}

/**
 * Email merge values for a session. `fields` are plain text (subject-safe: session_time is
 * the primary zone only); the body replaces {{session_time}} with sessionTimeHtml (HTML
 * templates) or sessionTimeText (plain-text templates: primary line, then other zones).
 */
export function sessionTimeMergeFields(startsAt, endsAt, eventTimezone) {
  const times = formatSessionTimes(startsAt, endsAt, eventTimezone);
  if (!times) {
    return {
      fields: { session_date: 'TBD', session_time: 'TBD', session_time_primary: 'TBD', session_timezones: '' },
      sessionTimeHtml: 'TBD',
      sessionTimeText: 'TBD',
    };
  }
  return {
    fields: {
      session_date: times.primary.dateLabel,
      session_time: times.primary.timeRangeLabel,
      session_time_primary: times.primary.timeRangeLabel,
      session_timezones: times.others.map((o) => o.timeRangeLabel).join(' · '),
    },
    sessionTimeHtml: times.html,
    sessionTimeText: times.plainText,
  };
}

/** Dropdown / single-line label: "Sun, Oct 4 · 10:00 AM – 12:00 PM EDT (also 9:00 AM CDT · 7:00 AM PDT)". */
export function formatSessionOptionLabel(session) {
  const start = toDate(session?.starts_at);
  if (!start) return '—';
  const tz = resolvePmTimezone(session?.timezone);
  const day = fmt(start, tz, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day} · ${formatSessionTimesCompact(session.starts_at, session.ends_at, tz)}`;
}
